import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PdfDocumentSection from "../components/PdfDocumentSection";
import usePdfConfig from "../hooks/usePdfConfig";
import "./DocumentsPage.css";
import { DEFAULT_PDF_PAGE_SLUG, normalizePdfPageSlug } from "../utils/pdfRouting";
import {
  getPdfPageDescriptionFromDocuments,
  getPdfPageMetaFromDocuments,
  getPdfDocumentsForPageSlug,
  getPdfPageSlugsFromDocuments,
  getPdfPageTitleFromDocuments,
} from "../utils/pdfDocuments";
import { setMetaName, setMetaProperty, setTitle } from "../utils/head";
import { useMetaData } from "../hooks/useMetaData";

export default function DocumentsPage({ pageSlug }) {
  const params = useParams();
  const slugFromRoute = params?.pdfPageSlug;
  const hasExplicitRouteSlug = Boolean(
    typeof slugFromRoute === "string" && slugFromRoute.trim()
  );
  const slugCandidate =
    typeof pageSlug === "string" && pageSlug.trim()
      ? pageSlug
      : slugFromRoute || DEFAULT_PDF_PAGE_SLUG;
  const resolvedPageSlug = useMemo(
    () => normalizePdfPageSlug(slugCandidate),
    [slugCandidate]
  );
  const { pdfConfig, loading, error } = usePdfConfig();
  const globalMeta = useMetaData();
  const documents = useMemo(
    () => getPdfDocumentsForPageSlug(pdfConfig?.documents, resolvedPageSlug),
    [pdfConfig?.documents, resolvedPageSlug]
  );
  const pageTitle = useMemo(
    () => getPdfPageTitleFromDocuments(pdfConfig?.documents, resolvedPageSlug),
    [pdfConfig?.documents, resolvedPageSlug]
  );
  const pageDescription = useMemo(
    () => getPdfPageDescriptionFromDocuments(pdfConfig?.documents, resolvedPageSlug),
    [pdfConfig?.documents, resolvedPageSlug]
  );
  const pageMeta = useMemo(
    () => getPdfPageMetaFromDocuments(pdfConfig?.documents, resolvedPageSlug),
    [pdfConfig?.documents, resolvedPageSlug]
  );
  const availableSlugs = useMemo(
    () => getPdfPageSlugsFromDocuments(pdfConfig?.documents),
    [pdfConfig?.documents]
  );
  const isConfiguredPageSlug = useMemo(
    () => availableSlugs.includes(resolvedPageSlug),
    [availableSlugs, resolvedPageSlug]
  );

  useEffect(() => {
    if (loading) return undefined;

    const fallbackTitle =
      (globalMeta?.metaTitle || globalMeta?.siteTitle || "Website").trim();
    const fallbackDescription = (globalMeta?.metaDescription || "").trim();
    const fallbackKeywords = (globalMeta?.metaKeywords || "").trim();

    const nextTitle =
      (pageMeta?.metaTitle || pageTitle || fallbackTitle || "Documents").trim();
    const nextDescription = (
      pageMeta?.metaDescription ||
      pageDescription ||
      fallbackDescription
    ).trim();
    const nextKeywords = (pageMeta?.metaKeywords || fallbackKeywords).trim();

    setTitle(nextTitle);
    setMetaName("description", nextDescription);
    setMetaName("keywords", nextKeywords);
    setMetaProperty("og:title", nextTitle);
    setMetaProperty("og:description", nextDescription);
    setMetaProperty("og:type", "website");

    return () => {
      const globalTitle =
        (globalMeta?.metaTitle || globalMeta?.siteTitle || "Website").trim();
      setTitle(globalTitle);
      setMetaName("description", (globalMeta?.metaDescription || "").trim());
      setMetaName("keywords", (globalMeta?.metaKeywords || "").trim());
      setMetaProperty("og:title", globalTitle);
      setMetaProperty(
        "og:description",
        (globalMeta?.metaDescription || "").trim()
      );
      setMetaProperty("og:type", "website");
    };
  }, [
    loading,
    pageMeta?.metaTitle,
    pageMeta?.metaDescription,
    pageMeta?.metaKeywords,
    pageTitle,
    pageDescription,
    globalMeta?.metaTitle,
    globalMeta?.metaDescription,
    globalMeta?.metaKeywords,
    globalMeta?.siteTitle,
  ]);

  if (loading) {
    return <div className="docs-state">Loading documents…</div>;
  }

  if (error) {
    return <div className="docs-state docs-error">{error}</div>;
  }

  if (
    hasExplicitRouteSlug &&
    resolvedPageSlug !== DEFAULT_PDF_PAGE_SLUG &&
    !documents.length &&
    !isConfiguredPageSlug
  ) {
    return (
      <div className="docs-page">
        <div className="docs-shell">
          <h1 className="docs-page-title">Page not found</h1>
          <div className="docs-empty">
            No published PDF page exists at <code>/{resolvedPageSlug}</code>.
            {availableSlugs.length > 0 ? (
              <div className="docs-empty-links">
                <span>Available pages:</span>
                <div className="docs-empty-link-list">
                  {availableSlugs.map((slug) => (
                    <Link key={slug} to={`/${slug}`} className="docs-empty-link">
                      /{slug}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="docs-empty-links">
                <Link to="/" className="docs-empty-link">
                  Go to Home
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="docs-page">
      <div className="docs-shell">
        <h1 className="docs-page-title">{pageTitle || "Documents"}</h1>
        {pageDescription ? (
          <p className="docs-page-desc">{pageDescription}</p>
        ) : null}

        {documents.length ? (
          <PdfDocumentSection
            documents={documents}
            title={pageTitle || "Documents"}
            subtitle="Browse available PDF cards and click to open the full document."
            mode="page"
            styleConfig={pdfConfig?.styleConfig}
          />
        ) : (
          <div className="docs-empty">
            No PDF is currently published for <code>/{resolvedPageSlug}</code>. Check
            PDF config: enabled item, placement set to dedicated page, same slug, and a
            valid uploaded filename or external URL.
            {availableSlugs.length > 0 ? (
              <div className="docs-empty-links">
                <span>Available pages:</span>
                <div className="docs-empty-link-list">
                  {availableSlugs.map((slug) => (
                    <Link key={slug} to={`/${slug}`} className="docs-empty-link">
                      /{slug}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
